/**
 * Utility functions for patient search functionality.
 * Provides contains matching with punctuation tolerance.
 */

interface SearchablePatient {
  patient_id?: string;
  first_name?: string;
  last_name?: string;
  date_of_birth?: string;
}

/**
 * Normalizes a string for search by:
 * - Converting to lowercase
 * - Removing punctuation (hyphens, dashes, apostrophes)
 * - Normalizing whitespace
 */
export const normalizeString = (str: string): string => {
  return str
    .toLowerCase()
    .replace(/[-–''"]/g, '') // Remove hyphens, dashes, apostrophes
    .replace(/\s+/g, ' ')     // Normalize whitespace
    .trim();
};

/**
 * Checks if a patient matches the search query.
 * Searches across first name, last name, full name, and date of birth.
 * Uses contains matching (substring search) with punctuation tolerance.
 */
export const matchesPatientSearch = (patient: SearchablePatient, query: string): boolean => {
  const normalizedQuery = normalizeString(query);
  if (!normalizedQuery) return true;

  const patientId = normalizeString(patient.patient_id || '');
  const firstName = normalizeString(patient.first_name || '');
  const lastName = normalizeString(patient.last_name || '');
  const fullName = normalizeString(`${patient.first_name || ''} ${patient.last_name || ''}`);
  const dob = patient.date_of_birth || '';

  // Check if query matches any field (contains matching)
  return (
    patientId.includes(normalizedQuery) ||
    firstName.includes(normalizedQuery) ||
    lastName.includes(normalizedQuery) ||
    fullName.includes(normalizedQuery) ||
    dob.includes(normalizedQuery) ||
    normalizeString(dob).includes(normalizedQuery)
  );
};
